// frontend/src/superadmin/SuperAdminAIChat.js
// UrbanIA - Widget de Chat con IA Local (DeepSeek R1)
// UrbanQ v1.9.5.6 - Con selector de modelos AI
//
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_URL } from '../config/api';
import './SuperAdminAIChat.css';

// ─────────────────────────────────────────────
// CONFIGURACIÓN DE MODELOS AI
// ─────────────────────────────────────────────
const AI_MODELS = [
  { id: 'urbanq-ia', name: 'UrbanQ IA', size: '8b', prodEnabled: true },
  { id: 'urbanq-ia:v2', name: 'UrbanIA v2 ★', size: '8b', prodEnabled: true },
  { id: 'deepseek-r1:7b', name: 'DeepSeek R1', size: '7b', prodEnabled: false },
];

// Detectar si estamos en producción (nextunicodecorp.com)
const isProduction = (process.env.REACT_APP_API_URL || '').includes('nextunicodecorp');
const DEFAULT_MODEL = 'urbanq-ia';

// ─────────────────────────────────────────────
// SUGERENCIAS CONTEXTUALES (SuperAdmin)
// ─────────────────────────────────────────────
const DEFAULT_SUGGESTIONS = [
  { text: '📊 Ver estadísticas', section: 'stats' },
  { text: '🏢 Condominios', section: 'condominiums' },
  { text: '📋 Reportería', section: 'reports' },
];

// ═══════════════════════════════════════════════════════════════
// SUGERENCIAS CONTEXTUALES PARA SUPERADMIN
// ═══════════════════════════════════════════════════════════════
const SUGGESTIONS = {
  INITIAL: [
    { text: "📊 Resumen del sistema", icon: "📊", section: 'stats' },
    { text: "🏢 Estado condominios", icon: "🏢", section: 'condominiums' },
    { text: "💰 Pagos pendientes", icon: "💰", section: 'reports' },
    { text: "📈 Ingresos del mes", icon: "📈", section: 'stats' }
  ],
  RESUMEN: [
    { text: "🏢 Condominios activos", icon: "🏢", section: 'condominiums' },
    { text: "👥 Total usuarios", icon: "👥", section: 'admins' },
    { text: "💰 Deuda global", icon: "💰", section: 'reports' },
    { text: "⚠️ Subs por vencer", icon: "⚠️", section: 'condominiums' }
  ],
  CONDOMINIOS: [
    { text: "🏆 Top por ingresos", icon: "🏆", section: 'stats' },
    { text: "⚠️ Con más deuda", icon: "⚠️", section: 'reports' },
    { text: "🆕 Nuevos este mes", icon: "🆕", section: 'condominiums' },
    { text: "📊 Activos vs inactivos", icon: "📊", section: 'stats' }
  ],
  USUARIOS: [
    { text: "👥 Por rol", icon: "👥", section: 'admins' },
    { text: "📈 Registros del mes", icon: "📈", section: 'stats' },
    { text: "⚠️ Con deuda alta", icon: "⚠️", section: 'reports' },
    { text: "🏢 Por condominio", icon: "🏢", section: 'condominiums' }
  ],
  PAGOS: [
    { text: "💰 Ingresos hoy", icon: "💰", section: 'stats' },
    { text: "📊 vs mes anterior", icon: "📊", section: 'reports' },
    { text: "⚠️ Pendientes críticos", icon: "⚠️", section: 'reports' },
    { text: "📈 Tendencia", icon: "📈", section: 'stats' }
  ],
  SUSCRIPCIONES: [
    { text: "✅ Activas", icon: "✅", section: 'condominiums' },
    { text: "⚠️ Por vencer", icon: "⚠️", section: 'condominiums' },
    { text: "❌ Expiradas", icon: "❌", section: 'reports' },
    { text: "💰 Ingresos subs", icon: "💰", section: 'stats' }
  ],
  GENERAL: [
    { text: "📊 Resumen completo", icon: "📊", section: 'stats' },
    { text: "⚠️ Alertas", icon: "⚠️", section: 'reports' },
    { text: "💰 Estado financiero", icon: "💰", section: 'stats' },
    { text: "📈 Métricas del día", icon: "📈", section: 'stats' }
  ]
};

// Genera sugerencias basadas en el contenido de la respuesta del bot
const getContextualSuggestions = (botResponse) => {
  const text = (botResponse || '').toLowerCase();
  const suggestions = [];

  if (text.includes('condominio') || text.includes('conjunto') || text.includes('residencial')) {
    suggestions.push({ text: '🏢 Ver condominios', section: 'condominiums' });
  }
  if (text.includes('pago') || text.includes('moroso') || text.includes('deuda') || text.includes('pendiente') || text.includes('cobr')) {
    suggestions.push({ text: '📋 Reportería', section: 'reports' });
  }
  if (text.includes('admin') || text.includes('administrador')) {
    suggestions.push({ text: '👥 Ver administradores', section: 'admins' });
  }
  if (text.includes('estadística') || text.includes('ingreso') || text.includes('total') || text.includes('resumen')) {
    suggestions.push({ text: '📊 Ver estadísticas', section: 'stats' });
  }
  if (text.includes('respaldo') || text.includes('backup') || text.includes('copia')) {
    suggestions.push({ text: '💾 Respaldos', section: 'backups' });
  }
  if (text.includes('configuración') || text.includes('ajuste') || text.includes('config')) {
    suggestions.push({ text: '⚙️ Configuración', section: 'settings' });
  }
  if (text.includes('suscripci') || text.includes('venc') || text.includes('expir')) {
    suggestions.push({ text: '🏢 Condominios', section: 'condominiums' });
  }

  // Si no hay suficientes sugerencias contextuales, usar default
  if (suggestions.length < 2) {
    return DEFAULT_SUGGESTIONS;
  }

  // Eliminar duplicados y limitar a 4
  const unique = suggestions.filter((s, i, arr) =>
    arr.findIndex(x => x.section === s.section) === i
  );
  return unique.slice(0, 4);
};

const SuperAdminAIChat = ({ setActiveSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: 'Hola! Soy UrbanIA, tu asistente inteligente de UrbanQ. Puedo ayudarte con estadisticas del sistema, analisis de condominios, pagos pendientes y cualquier consulta de la plataforma. En que te ayudo?',
      thinking: '',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showThinking, setShowThinking] = useState({});
  const [contextSuggestions, setContextSuggestions] = useState(DEFAULT_SUGGESTIONS);

  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll cuando hay nuevos mensajes
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Manejar clase del body para modo fullscreen (efecto 3D)
  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('urbanq-ai-focus-mode');
    } else {
      document.body.classList.remove('urbanq-ai-focus-mode');
    }
    // Cleanup al desmontar
    return () => {
      document.body.classList.remove('urbanq-ai-focus-mode');
    };
  }, [isFullscreen]);

  // Obtener token de autenticacion
  const getToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  };

  // Toggle del panel de thinking
  const toggleThinking = (id) => {
    setShowThinking(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Ciclar entre modos: normal -> expandido -> fullscreen -> normal
  const cycleViewMode = () => {
    if (!isExpanded && !isFullscreen) {
      // normal -> expandido
      setIsExpanded(true);
      setIsFullscreen(false);
    } else if (isExpanded && !isFullscreen) {
      // expandido -> fullscreen
      setIsExpanded(false);
      setIsFullscreen(true);
    } else {
      // fullscreen -> normal
      setIsExpanded(false);
      setIsFullscreen(false);
    }
  };

  // Obtener icono segun el modo actual
  const getViewModeIcon = () => {
    if (isFullscreen) return '\u26F6'; // fullscreen icon
    if (isExpanded) return '\u229F';   // contraer
    return '\u229E';                    // expandir
  };

  // Obtener titulo segun el modo actual
  const getViewModeTitle = () => {
    if (isFullscreen) return 'Salir de pantalla completa';
    if (isExpanded) return 'Pantalla completa';
    return 'Expandir';
  };

  // Limpiar conversacion
  const clearChat = () => {
    setMessages([{
      id: 1,
      role: 'assistant',
      content: 'Hola! Soy UrbanIA, tu asistente inteligente de UrbanQ. Puedo ayudarte con estadisticas del sistema, analisis de condominios, pagos pendientes y cualquier consulta de la plataforma. En que te ayudo?',
      thinking: '',
      timestamp: new Date()
    }]);
    setHistory([]);
    setShowThinking({});
    setContextSuggestions(DEFAULT_SUGGESTIONS);
  };

  // Manejar click en sugerencia de navegación
  const handleSuggestionClick = (suggestion) => {
    if (setActiveSection && suggestion.section) {
      setActiveSection(suggestion.section);
      // Opcional: minimizar chat al navegar
      // setIsOpen(false);
    }
  };

  // Enviar mensaje
  const handleSend = async () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || isLoading) return;

    // Crear mensaje del usuario
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: trimmedMessage,
      timestamp: new Date()
    };

    // Agregar mensaje a la lista y limpiar input
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Preparar el API endpoint
      const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
      const apiPrefix = isDevelopment ? '' : '/api';
      const endpoint = `${API_URL}${apiPrefix}/agent/chat`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          message: trimmedMessage,
          history: history,
          model: selectedModel
        })
      });

      const data = await response.json();

      // Crear mensaje del bot
      const botMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.response || 'Lo siento, no pude procesar tu consulta.',
        thinking: data.thinking || '',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMsg]);

      // Actualizar sugerencias contextuales basadas en la respuesta
      setContextSuggestions(getContextualSuggestions(data.response || ''));

      // Actualizar historial (mantener ultimos 20 mensajes)
      setHistory(prev => [
        ...prev,
        { role: 'user', content: trimmedMessage },
        { role: 'assistant', content: data.response || '' }
      ].slice(-20));

    } catch (error) {
      console.error('[UrbanIA] Error:', error);
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Hubo un problema de conexion. Por favor intenta de nuevo en unos momentos.',
        thinking: '',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
      setContextSuggestions(DEFAULT_SUGGESTIONS);
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar Enter para enviar
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="sa-ai-container">
      {/* Ventana del Chat */}
      {isOpen && (
        <div className={`sa-ai-window ${isExpanded ? 'expanded' : ''} ${isFullscreen ? 'fullscreen' : ''}`}>
          {/* Header */}
          <div className="sa-ai-header">
            <div className="sa-ai-header-left">
              <div className="sa-ai-header-avatar">
                <span role="img" aria-label="robot">&#129302;</span>
              </div>
              <div className="sa-ai-header-info">
                <span className="sa-ai-header-name">UrbanIA</span>
                {/* Selector de Modelo */}
                <div className="sa-ai-model-selector">
                  <button
                    className="sa-ai-model-btn"
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                    title="Seleccionar modelo de IA"
                  >
                    <span className="sa-ai-status-dot"></span>
                    {AI_MODELS.find(m => m.id === selectedModel)?.name || 'UrbanQ IA'} · {AI_MODELS.find(m => m.id === selectedModel)?.size || '8b'}
                    <span className="sa-ai-model-arrow">{showModelDropdown ? '▲' : '▼'}</span>
                  </button>
                  {showModelDropdown && (
                    <div className="sa-ai-model-dropdown">
                      {AI_MODELS.map(model => {
                        const isEnabled = isProduction ? model.prodEnabled : true;
                        const isSelected = model.id === selectedModel;
                        return (
                          <button
                            key={model.id}
                            className={`sa-ai-model-option ${isSelected ? 'selected' : ''} ${!isEnabled ? 'disabled' : ''}`}
                            onClick={() => {
                              if (isEnabled) {
                                setSelectedModel(model.id);
                                setShowModelDropdown(false);
                              }
                            }}
                            disabled={!isEnabled}
                            title={!isEnabled ? 'Próximamente en producción' : `Usar ${model.name} ${model.size}`}
                          >
                            <span className="sa-ai-model-name">{model.name} · {model.size}</span>
                            {isSelected && <span className="sa-ai-model-check">✓</span>}
                            {!isEnabled && <span className="sa-ai-model-soon">Próximamente</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="sa-ai-header-actions">
              <button
                className="sa-ai-btn-icon"
                onClick={cycleViewMode}
                title={getViewModeTitle()}
              >
                {getViewModeIcon()}
              </button>
              <button
                className="sa-ai-btn-icon"
                onClick={clearChat}
                title="Limpiar chat"
              >
                &#128465;
              </button>
              <button
                className="sa-ai-btn-icon sa-ai-btn-close"
                onClick={() => setIsOpen(false)}
                title="Cerrar"
              >
                &#10005;
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="sa-ai-messages" ref={messagesContainerRef}>
            {messages.map(msg => (
              <div key={msg.id} className={`sa-ai-msg-wrap ${msg.role}`}>
                {/* Burbuja del mensaje */}
                <div className={`sa-ai-bubble ${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <div className="sa-ai-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>

                {/* Boton thinking - solo si tiene contenido */}
                {msg.role === 'assistant' && msg.thinking && (
                  <div className="sa-ai-think-container">
                    <button
                      className="sa-ai-think-toggle"
                      onClick={() => toggleThinking(msg.id)}
                    >
                      {showThinking[msg.id]
                        ? '\u26A1 Ocultar razonamiento \u25B2'
                        : '\u26A1 Ver razonamiento \u25BC'}
                    </button>

                    {showThinking[msg.id] && (
                      <div className="sa-ai-think-panel">
                        <div className="sa-ai-think-header">
                          <span role="img" aria-label="bolt">&#9889;</span> UrbanIA procesando...
                          <span className="sa-ai-think-cursor">&#9608;</span>
                        </div>
                        <div className="sa-ai-think-body">
                          {msg.thinking.split('\n').filter(l => l.trim()).map((line, i, arr) => {
                            const fromEnd = arr.length - 1 - i;
                            const opacityClass = fromEnd === 0 ? 'line-0'
                              : fromEnd === 1 ? 'line-1'
                              : fromEnd === 2 ? 'line-2' : 'line-3';
                            return (
                              <div key={i} className={`sa-ai-think-line ${opacityClass}`}>
                                &gt; {line}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="sa-ai-msg-wrap assistant">
                <div className="sa-ai-bubble assistant">
                  <div className="sa-ai-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p className="sa-ai-loading-hint">Consultando datos del sistema...</p>
                </div>
              </div>
            )}

            {/* Sugerencias contextuales PERMANENTES */}
            {contextSuggestions.length > 0 && (
              <div className="sa-ai-suggestions-permanent">
                <p className="sa-ai-suggestions-label">
                  {isLoading ? 'Mientras esperas:' : 'Accesos rápidos:'}
                </p>
                <div className="sa-ai-suggestions-list">
                  {contextSuggestions.map((s, i) => (
                    <button
                      key={i}
                      className="sa-ai-chip"
                      onClick={() => handleSuggestionClick(s)}
                    >
                      {s.text}
                      <span className="sa-ai-chip-arrow">→</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="sa-ai-input-area">
            <input
              type="text"
              className="sa-ai-input"
              placeholder="Pregunta sobre el sistema..."
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              maxLength={500}
            />
            <button
              className="sa-ai-btn-send"
              onClick={handleSend}
              disabled={isLoading || !inputValue.trim()}
            >
              {isLoading ? '\u23F3' : '\u25B6'}
            </button>
          </div>
        </div>
      )}

      {/* Burbuja Flotante */}
      <button
        className={`sa-ai-bubble-btn ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="UrbanIA - Asistente Inteligente"
      >
        <span className="sa-ai-bubble-icon">
          {isOpen ? '\u2715' : '\u{1F916}'}
        </span>
        {!isOpen && <span className="sa-ai-bubble-ping"></span>}
      </button>
    </div>
  );
};

export default SuperAdminAIChat;
